<template>
  <div class="document-preview">
    <!-- Toolbar section -->
    <div class="document-toolbar">
      <h2>{{ title }}</h2>
      <div class="document-actions">
        <!-- Enhanced zoom controls -->
        <div class="zoom-controls">
          <button @click="decreaseZoom" class="zoom-btn" :disabled="zoomLevel <= minZoomLevel" title="縮小">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
          </button>
          <span class="zoom-level">{{ Math.round(zoomLevel * 100) }}%</span>
          <button @click="increaseZoom" class="zoom-btn" :disabled="zoomLevel >= maxZoomLevel" title="放大">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="11" y1="8" x2="11" y2="14"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
          </button>
        </div>
        <button @click="resetZoom" class="action-btn" title="重設檢視">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 2v6h-6"></path>
            <path d="M3 12a9 9 0 0 1 15-6.7l3 2.7"></path>
            <path d="M3 22v-6h6"></path>
            <path d="M21 12a9 9 0 0 1-15 6.7l-3-2.7"></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- PDF display container -->
    <div class="pdf-container" :class="{ 'zoomed': zoomLevel > 1.0 }">
      <!-- PDF initial loading indicator -->
      <div v-if="isLoadingDocument" class="document-loading">
        <div class="spinner"></div>
        <p class="loading-text">正在載入文件...</p>
      </div>

      <!-- PDF view when document is loaded but page is loading -->
      <canvas v-show="!isLoadingDocument" ref="pdfCanvas" class="pdf-canvas"></canvas>

      <!-- Page loading indicator (when changing pages) -->
      <div v-if="isLoadingPage && !isLoadingDocument" class="page-loading">
        <div class="spinner small"></div>
      </div>
    </div>

    <!-- Navigation controls -->
    <div class="document-navigation">
      <button @click="prevPage" :disabled="currentPage === 1 || isLoadingDocument" class="nav-btn">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        上一頁
      </button>
      <div class="page-indicator">
        <input
            type="number"
            v-model.number="currentPageInput"
            @change="goToPage(currentPageInput)"
            :min="1"
            :max="totalPages"
            class="page-input"
            :disabled="isLoadingDocument"
        />
        <span class="page-total">/ {{ totalPages || '-' }}</span>
      </div>
      <button @click="nextPage" :disabled="currentPage === totalPages || isLoadingDocument" class="nav-btn">
        下一頁
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import pdfService from '@/services/pdf.service';
import { debounce } from 'lodash'; // Add this dependency

export default {
  name: 'DocumentViewer',
  props: {
    documentUrl: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: '貸款合約'
    },
    token: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      pdfDoc: null,
      currentPage: 1,
      currentPageInput: 1,
      totalPages: 0,
      // Enhanced zoom levels
      minZoomLevel: 0.5,
      maxZoomLevel: 3.0,
      zoomLevel: 1.0,
      zoomStep: 0.25,
      isLoadingPage: false,
      isLoadingDocument: true,
      pageRenderPromise: null,
      pdfLoaded: false,
      error: null,
      resizeTimer: null,
      pixelRatio: window.devicePixelRatio || 1,
      // New properties for optimization
      pageCache: {},
      pageLoadQueue: [],
      isPreloadingPages: false,
      renderId: 0 // To track and potentially cancel outdated renders
    };
  },
  computed: {
    /**
     * Determine which document URL to use
     * For development, use the provided documentUrl
     * In production, you would use the token to fetch from backend
     */
    resolvedDocumentUrl() {
      return this.documentUrl || '';
    }
  },
  watch: {
    documentUrl() {
      this.resetViewerState();
      this.loadDocument();
    },
    token() {
      if (this.token) {
        this.resetViewerState();
        this.loadDocument();
      }
    }
  },
  emits: ['error', 'document-loaded', 'expired-url'],
  created() {
    // Create debounced versions of handlers
    this.debouncedRenderPage = debounce(this.renderPage.bind(this), 150);
    this.debouncedHandleWindowResize = debounce(this.handleWindowResize, 200);
  },
  methods: {
    // Reset viewer state when loading a new document
    resetViewerState() {
      this.isLoadingDocument = true;
      this.pdfLoaded = false;
      this.error = null;
      this.currentPage = 1;
      this.currentPageInput = 1;
      this.totalPages = 0;
      this.zoomLevel = 1.0;
      this.pageCache = {}; // Clear page cache
      this.pageLoadQueue = [];
      this.isPreloadingPages = false;
      this.renderId++; // Increment to cancel any ongoing renders
    },

    setupTouchHandlers() {
      const navButtons = document.querySelectorAll('.nav-btn');
      navButtons.forEach(button => {
        button.addEventListener('touchend', (e) => {
          e.preventDefault(); // Prevents default touch behavior
          // Trigger the appropriate action based on button
          if (button.textContent.includes('上一頁')) {
            this.prevPage();
          } else if (button.textContent.includes('下一頁')) {
            this.nextPage();
          }
        });
      });
    },

    async renderPage(pageNumber, lowQuality = false) {
      if (!this.pdfDoc || !this.pdfLoaded) {
        console.error('未載入 PDF 文件');
        return;
      }

      const currentRenderId = ++this.renderId;

      try {
        this.isLoadingPage = true;

        const canvas = this.$refs.pdfCanvas;
        if (!canvas) {
          console.error('找不到 PDF 畫布元素');
          return;
        }

        const container = canvas.parentElement;

        console.log(`開始渲染頁面 ${pageNumber}，縮放級別: ${this.zoomLevel}, 低品質模式: ${lowQuality}`);

        // Check if render was superseded by a newer request
        if (currentRenderId !== this.renderId) {
          console.log('取消過時的渲染請求');
          return;
        }

        let page;
        // Use cached page if available
        if (this.pageCache[pageNumber]) {
          page = this.pageCache[pageNumber];
          console.log(`使用緩存的頁面 ${pageNumber}`);
        } else {
          page = await this.pdfDoc.getPage(pageNumber);
          this.pageCache[pageNumber] = page; // Store in cache
          console.log(`載入並緩存頁面 ${pageNumber}`);
        }

        // Check again if render was superseded
        if (currentRenderId !== this.renderId) {
          console.log('取消過時的渲染請求');
          return;
        }

        // Calculate optimal scale based on container width
        let baseScale = pdfService.calculateOptimalScale(page, container, 30);

        // Reduce quality for initial load
        if (lowQuality) baseScale = baseScale * 0.8;

        // Apply user zoom factor
        const finalScale = baseScale * this.zoomLevel;

        // Get the viewport with the calculated scale
        const viewport = page.getViewport({ scale: finalScale });

        // Get the pixel ratio for high-DPI rendering - use lower ratio for quick rendering
        const pixelRatio = lowQuality ? 1 : (window.devicePixelRatio || 1);

        // Set canvas backing store dimensions (actual pixels)
        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);

        // Set canvas display size (CSS pixels)
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        // Get the canvas context with optimized settings
        const ctx = canvas.getContext('2d', {
          alpha: false,
          willReadFrequently: false // Performance hint
        });

        // Clear any previous content
        ctx.save();
        ctx.fillStyle = 'rgb(255, 255, 255)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();

        // Scale the context to account for the pixel ratio
        ctx.scale(pixelRatio, pixelRatio);

        // Use the correct annotation mode from PDF service
        const annotationMode = lowQuality
            ? pdfService.annotationMode.DISABLE
            : pdfService.annotationMode.ENABLE_FORMS;

        // Prepare render context with correct properties
        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
          annotationMode: annotationMode,
          enableTextLayer: !lowQuality,
        };

        // Render the page
        console.log('開始頁面渲染操作');
        const renderTask = page.render(renderContext);

        // Store the render promise to potentially cancel it
        this.pageRenderPromise = renderTask.promise;

        await this.pageRenderPromise;

        // Final check if this render is still relevant
        if (currentRenderId !== this.renderId) {
          console.log('渲染完成但已過時，不更新狀態');
          return;
        }

        console.log('頁面渲染完成');

        this.currentPage = pageNumber;
        this.currentPageInput = pageNumber; // 保持輸入框與當前頁面同步
        this.isLoadingPage = false;

        // Clean up memory
        this.cleanupCache();

        // Preload adjacent pages after rendering current page
        if (!lowQuality) {
          this.preloadAdjacentPages(pageNumber);
        }
      } catch (err) {
        console.error('渲染頁面錯誤:', err);
        // 只有當不是取消錯誤時才顯示錯誤消息
        if (err.message !== 'Rendering cancelled' && currentRenderId === this.renderId) {
          this.handleError(`顯示文件時出錯。${err.message || '未知錯誤'}`);
        }
        this.isLoadingPage = false;
      }
    },

    async loadDocument() {
      if (!this.resolvedDocumentUrl) {
        this.handleError('未提供文件URL');
        return;
      }

      // Set loading state immediately
      this.isLoadingDocument = true;
      console.log('開始載入文檔:', this.resolvedDocumentUrl);

      try {
        // Use a race between loading and a small timeout to show something ASAP
        const loadPromise = pdfService.loadDocument(this.resolvedDocumentUrl);
        const timeoutPromise = new Promise(resolve => {
          setTimeout(() => {
            // Just resolve with a signal value, we'll still wait for the actual load
            resolve('timeout');
          }, 500); // Half-second timeout to ensure user sees something
        });

        const result = await Promise.race([loadPromise, timeoutPromise]);

        // If we got the timeout signal, we continue waiting for the actual load
        // but at least the loading state is visible to the user
        if (result === 'timeout') {
          this.pdfDoc = await loadPromise;
        } else {
          this.pdfDoc = result;
        }

        this.totalPages = this.pdfDoc.numPages;
        this.pdfLoaded = true;

        console.log('文檔載入成功，總頁數:', this.totalPages);

        // Render first page immediately at lower quality for faster display
        await this.renderPage(1, true);
        this.isLoadingDocument = false;

        // Then improve quality after initial display
        setTimeout(() => this.renderPage(this.currentPage, false), 100);

        // 通知父組件文件已加載
        this.$emit('document-loaded', {
          totalPages: this.totalPages,
          currentPage: this.currentPage,
          documentId: this.token || 'sample'
        });

        // Start preloading other pages for faster navigation
        await this.preloadAdjacentPages(1);
      } catch (err) {
        console.error('PDF 載入錯誤:', err);

        if (err.message.includes('過期') || err.message.includes('訪問') ||
            err.message.includes('401') || err.message.includes('403')) {
          this.$emit('expired-url', err.message);
        } else {
          this.handleError(`無法載入文件: ${err.message || '未知錯誤'}`);
        }

        this.isLoadingDocument = false;
      }
    },

    checkUrlValidity() {
      if (!this.resolvedDocumentUrl) return;

      // Simple HEAD request to check if URL is still valid
      fetch(this.resolvedDocumentUrl, { method: 'HEAD' })
          .then(response => {
            if (!response.ok && (response.status === 401 || response.status === 403)) {
              console.warn('URL appears to be expired, status:', response.status);
              this.$emit('expired-url', '文檔訪問鏈接可能已過期，需要刷新');
            }
          })
          .catch(err => {
            // Only emit for CORS or network errors that might indicate expiry
            if (err.message.includes('CORS') || err.message.includes('network')) {
              this.$emit('expired-url', `訪問文檔時出錯: ${err.message}`);
            }
          });
    },

    handleError(message) {
      this.error = message;
      this.$emit('error', message);
    },

    // Enhanced zoom controls
    increaseZoom() {
      if (this.zoomLevel < this.maxZoomLevel) {
        this.zoomLevel = Math.min(this.maxZoomLevel, this.zoomLevel + this.zoomStep);
        this.renderPageWithRAF(this.currentPage);
      }
    },

    decreaseZoom() {
      if (this.zoomLevel > this.minZoomLevel) {
        this.zoomLevel = Math.max(this.minZoomLevel, this.zoomLevel - this.zoomStep);
        this.renderPageWithRAF(this.currentPage);
      }
    },

    resetZoom() {
      this.zoomLevel = 1.0;
      this.renderPageWithRAF(this.currentPage);
    },

    // Use requestAnimationFrame for smoother rendering
    renderPageWithRAF(pageNumber, lowQuality = false) {
      requestAnimationFrame(() => {
        this.renderPage(pageNumber, lowQuality);
      });
    },

    // Preload adjacent pages for faster navigation
    async preloadAdjacentPages(currentPage) {
      if (this.isPreloadingPages || !this.pdfDoc) return;

      this.isPreloadingPages = true;
      const pagesToPreload = [];

      // Preload next page
      if (currentPage < this.totalPages) pagesToPreload.push(currentPage + 1);

      // Preload previous page
      if (currentPage > 1) pagesToPreload.push(currentPage - 1);

      for (const pageNum of pagesToPreload) {
        if (!this.pageCache[pageNum]) {
          try {
            const page = await this.pdfDoc.getPage(pageNum);
            this.pageCache[pageNum] = page;
          } catch (err) {
            console.warn(`Failed to preload page ${pageNum}:`, err);
          }
        }
      }

      this.isPreloadingPages = false;
    },

    // Clean up memory by removing pages far from current view
    cleanupCache() {
      const currentPage = this.currentPage;
      const cacheKeys = Object.keys(this.pageCache).map(Number);

      // Keep only pages within a certain range
      for (const pageNum of cacheKeys) {
        if (Math.abs(pageNum - currentPage) > 3) {
          delete this.pageCache[pageNum];
        }
      }
    },

    // 跳轉到指定頁面
    goToPage(pageNumber) {
      const num = parseInt(pageNumber);
      if (isNaN(num)) {
        this.currentPageInput = this.currentPage;
        return;
      }

      if (num < 1) {
        this.currentPageInput = 1;
        this.renderPageWithRAF(1);
      } else if (num > this.totalPages) {
        this.currentPageInput = this.totalPages;
        this.renderPageWithRAF(this.totalPages);
      } else {
        this.renderPageWithRAF(num);
      }
    },

    // 頁面導航
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.renderPageWithRAF(this.currentPage + 1);
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.renderPageWithRAF(this.currentPage - 1);
      }
    },

    // 處理窗口調整大小事件 - now using debounced version
    handleWindowResize() {
      if (this.$refs.pdfCanvas && this.pdfDoc && this.pdfLoaded) {
        // 更新像素比率以應對屏幕變化
        this.pixelRatio = window.devicePixelRatio || 1;
        // 重新渲染當前PDF頁面以適應新的窗口大小
        this.renderPageWithRAF(this.currentPage);
      }
    },

    // Cancel any ongoing renders when component is destroyed
    cancelRenders() {
      this.renderId++; // This will make any ongoing renders obsolete
      if (this.pageRenderPromise) {
        // PDF.js render tasks can be cancelled
        if (typeof this.pageRenderPromise.cancel === 'function') {
          this.pageRenderPromise.cancel();
        }
        this.pageRenderPromise = null;
      }
    }
  },
  mounted() {
    // 載入文檔
    this.loadDocument();

    this.urlCheckInterval = setInterval(() => {
      // Only check if document is loaded
      if (this.pdfLoaded) {
        this.checkUrlValidity();
      }
    }, 180000);

    // Set up touch handlers for mobile
    this.$nextTick(() => {
      this.setupTouchHandlers();
    });

    // 添加窗口調整大小事件監聽器 - use debounced version
    window.addEventListener('resize', this.debouncedHandleWindowResize);
  },
  beforeUnmount() {
    // 移除窗口調整大小事件監聽器
    window.removeEventListener('resize', this.debouncedHandleWindowResize);

    if (this.urlCheckInterval) {
      clearInterval(this.urlCheckInterval);
      this.urlCheckInterval = null;
    }
    // Cancel any ongoing renders
    this.cancelRenders();

    // Clear any page cache to free memory
    this.pageCache = {};

    // 清除任何定時器
    clearTimeout(this.resizeTimer);
  }
}
</script>

<style scoped>
.document-preview {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(255, 158, 21, 0.15);
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.document-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 158, 21, 0.2);
  background-color: #fffaf0;
}

.document-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.zoom-controls {
  display: flex;
  align-items: center;
  background-color: white;
  border: 1px solid rgba(255, 158, 21, 0.5);
  border-radius: 4px;
  overflow: hidden;
}

.zoom-btn {
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #FF9E15;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.zoom-btn:hover:not(:disabled) {
  background-color: rgba(255, 158, 21, 0.1);
}

.zoom-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.zoom-level {
  font-size: 13px;
  color: #333;
  padding: 0 6px;
  min-width: 54px;
  text-align: center;
  user-select: none;
}

.action-btn {
  background: transparent;
  border: 1px solid rgba(255, 158, 21, 0.5);
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  color: #FF9E15;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background-color: rgba(255, 158, 21, 0.1);
  color: #FF9E15;
}

.action-btn:active {
  transform: scale(0.96);
}

.pdf-container {
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px 16px;
  background-color: #f9f9f9;
  overflow-x: auto;
  overflow-y: auto;
  min-height: 350px;
  transition: background-color 0.2s ease;
  will-change: transform;  /* Helps with rendering performance */
  transform: translateZ(0);  /* Forces GPU acceleration */
}

/* 縮放模式 */
.pdf-container.zoomed {
  overflow-x: auto;
  overflow-y: auto;
  background-color: #f5f5f5;
}

.pdf-canvas {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  background-color: white;
  pointer-events: none;
  user-select: none;
  display: block;
  max-width: none;
  border-radius: 2px;
  /* Improve image rendering quality */
  image-rendering: auto; /* Modern standard for image rendering */
  -webkit-font-smoothing: antialiased; /* For Chrome/Safari */
  -moz-osx-font-smoothing: grayscale;  /* For Firefox */
  text-rendering: optimizeLegibility;  /* Better text rendering */
  backface-visibility: hidden; /* Helps with anti-aliasing in some browsers */
}

/* Initial document loading state */
.document-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  min-width: 200px;
}

.loading-text {
  margin: 16px 0 0 0;
  color: #333;
  font-size: 15px;
  font-weight: 500;
}

/* Page changing loading state */
.page-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.1);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 158, 21, 0.2);
  border-top-color: #FF9E15;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner.small {
  width: 28px;
  height: 28px;
  border-width: 2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.document-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-top: 1px solid rgba(255, 158, 21, 0.2);
  background-color: #fffaf0;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: white;
  border: 1px solid #FF9E15;
  border-radius: 4px;
  padding: 8px 14px;
  color: #FF9E15;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  touch-action: manipulation;
}

.nav-btn:hover:not(:disabled) {
  background-color: rgba(255, 158, 21, 0.1);
}

.nav-btn:active:not(:disabled) {
  transform: scale(0.97);
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  border-color: rgba(255, 158, 21, 0.3);
}

.nav-btn svg {
  stroke: #FF9E15;
}

.page-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #333;
}

.page-input {
  width: 45px;
  text-align: center;
  border: 1px solid rgba(255, 158, 21, 0.4);
  border-radius: 4px;
  padding: 8px 4px;
  font-size: 14px;
  color: #333;
  transition: border-color 0.2s ease;
}

.page-input:focus {
  outline: none;
  border-color: #FF9E15;
  box-shadow: 0 0 0 2px rgba(255, 158, 21, 0.2);
}

.page-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.page-total {
  color: #666;
}

h2 {
  margin: 0;
  font-size: 17px;
  font-weight: 500;
  color: #333;
}

/* Tablet view */
@media (max-width: 768px) {
  .document-toolbar {
    padding: 12px 14px;
  }

  .pdf-container {
    padding: 16px 12px;
    min-height: 320px;
  }

  .document-navigation {
    padding: 12px 14px;
    touch-action: manipulation;
  }

  .nav-btn {
    padding: 8px 12px;
  }

  .document-loading {
    padding: 20px;
  }
}

/* Mobile view */
@media (max-width: 480px) {
  .document-toolbar {
    padding: 10px 12px;
  }

  .pdf-container {
    padding: 12px 10px;
    min-height: 280px;
  }

  .nav-btn {
    padding: 8px 10px;
    font-size: 13px;
  }

  .page-input {
    width: 36px;
    padding: 6px 4px;
    font-size: 13px;
  }

  h2 {
    font-size: 15px;
  }

  .action-btn, .zoom-btn {
    padding: 6px;
  }

  .zoom-level {
    min-width: 40px;
    font-size: 12px;
  }

  .action-btn svg, .zoom-btn svg {
    width: 14px;
    height: 14px;
  }

  .document-navigation {
    padding: 10px 12px;
  }

  .document-loading {
    padding: 18px;
    min-width: 180px;
  }

  .loading-text {
    font-size: 14px;
    margin-top: 14px;
  }

  .spinner {
    width: 32px;
    height: 32px;
  }
}

/* Small mobile view */
@media (max-width: 380px) {
  .document-toolbar {
    padding: 8px 10px;
  }

  h2 {
    font-size: 14px;
    max-width: 140px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .nav-btn {
    padding: 6px 8px;
    font-size: 12px;
  }

  .nav-btn svg {
    width: 14px;
    height: 14px;
  }

  .page-input {
    width: 32px;
    padding: 5px 3px;
    font-size: 12px;
  }

  .page-total {
    font-size: 12px;
  }

  .pdf-container {
    min-height: 250px;
  }

  .zoom-controls {
    margin-right: 4px;
  }

  .zoom-level {
    min-width: 36px;
    padding: 0 3px;
    font-size: 11px;
  }

  .document-loading {
    padding: 16px;
    min-width: 160px;
  }

  .loading-text {
    font-size: 13px;
  }
}
</style>